// Mock Regulatory Cell receipts — pure frontend data, no backend. Fresh
// build (replaces the deleted overlay-on-MaterialReceiptRow approach): every
// field the Regulatory screens need lives directly on the receipt.
//
// All dates are derived from REGULATORY_SEED_TODAY — never Date.now()/new Date().
// SLA = 3 business days from receivedDate. Against TODAY (2026-06-08, a
// Monday): receivedDate >= 06-05 leaves 2+ business days (GREEN); 06-03/06-04
// leaves 0-1 (AMBER); on/before 06-02 is already past due (BREACHED). Printed
// receipts are excluded from that read — they're already fulfilled.
import type { RegulatoryReceipt } from './regulatoryTypes';

export const REGULATORY_SEED_TODAY = '2026-06-08';

export const MOCK_REGULATORY_RECEIPTS: RegulatoryReceipt[] = [
  // ── UNIDENTIFIED (13): 8 exact CUSIP+LitCode matches, 3 fuzzy fund-name
  // matches, 2 no-match — see mockRegRefNumberReport.ts for the paired rows.
  {
    receiptId: 'RCT-5100001', materialType: 'REGULATORY', scanNo: '06072601', receivedDate: '2026-06-07',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan Ultra-Short Municipal Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'JPMSUM8-26', cusip: '46641Q101',
    quantity: 12, location: 'MB112', boxes: 1, carrier: 'FEDEX', tracking: '784410019921',
    counted: 'DM', distributor: 'Donnelly', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 6, loadStatus: 'New',
    history: [{ at: '2026-06-07', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100002', materialType: 'REGULATORY', scanNo: '06062601', receivedDate: '2026-06-06',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan Prime Money Market Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'JPMPROS3-26', cusip: '46641Q202',
    quantity: 30, location: 'MB113', boxes: 2, carrier: 'UPS', tracking: '1Z9991A0392',
    counted: 'DM', distributor: 'Broadridge', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    history: [{ at: '2026-06-06', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100003', materialType: 'REGULATORY', scanNo: '06082601', receivedDate: '2026-06-08',
    packingSlip: true, client: 'First Trust', bookName: 'First Trust Enhanced Short Maturity ETF',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'FTSUM5-26', cusip: '33739Q101',
    quantity: 18, location: 'MC201', boxes: 1, carrier: 'FEDEX', tracking: '784410019933',
    counted: 'RC', distributor: 'Mittera', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 4, loadStatus: 'New',
    history: [{ at: '2026-06-08', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100004', materialType: 'REGULATORY', scanNo: '06052601', receivedDate: '2026-06-05',
    packingSlip: false, errorCode: 'NO_PK_SLIP', client: 'First Trust', bookName: 'First Trust Municipal High Income ETF',
    material: 'SUPP', documentType: 'Supplement', litCode: 'FTSUPP2-26', cusip: '33739Q202',
    quantity: 22, location: 'MC202', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88213',
    counted: 'RC', distributor: 'DFS', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    history: [{ at: '2026-06-05', event: 'Received — scan intake (no packing slip)' }],
  },
  {
    receiptId: 'RCT-5100005', materialType: 'REGULATORY', scanNo: '06072602', receivedDate: '2026-06-07',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson Energy Infrastructure Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'KAESUM7-26', cusip: '486624101',
    quantity: 9, location: 'MB114', boxes: 1, carrier: 'FEDEX', tracking: '784410019945',
    counted: 'DM', distributor: 'Smart', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 8, loadStatus: 'New',
    history: [{ at: '2026-06-07', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100006', materialType: 'REGULATORY', scanNo: '06042601', receivedDate: '2026-06-04',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson BDC Investment Group',
    material: 'PROS', documentType: 'Prospectus', litCode: 'KAEPROS1-26', cusip: '486624202',
    quantity: 40, location: 'MB115', boxes: 2, carrier: 'UPS', tracking: '1Z9991A0405',
    counted: 'RC', distributor: 'Donnelly', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    history: [{ at: '2026-06-04', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100007', materialType: 'REGULATORY', scanNo: '06082602', receivedDate: '2026-06-08',
    packingSlip: true, client: 'Fidelity', bookName: 'Fidelity Government Money Market Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'FIDSUM9-26', cusip: '316175101',
    quantity: 60, location: 'MC203', boxes: 2, carrier: 'FEDEX', tracking: '784410019957',
    counted: 'DM', distributor: 'Broadridge', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 6, loadStatus: 'New',
    history: [{ at: '2026-06-08', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100008', materialType: 'REGULATORY', scanNo: '06062602', receivedDate: '2026-06-06',
    packingSlip: true, client: 'American Funds', bookName: 'American Funds Capital Income Builder',
    material: 'PROS', documentType: 'Prospectus', litCode: 'AFPROS4-26', cusip: '025080101',
    quantity: 15, location: 'MB116', boxes: 1, carrier: 'UPS', tracking: '1Z9991A0417',
    counted: 'RC', distributor: 'Mittera', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    history: [{ at: '2026-06-06', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100009', materialType: 'REGULATORY', scanNo: '06032601', receivedDate: '2026-06-03',
    packingSlip: true, client: 'YieldMax', bookName: 'YieldMax NVDA Option Income Strategy ETF',
    material: '30E3', documentType: '30e-3', litCode: 'N/A', ticker: 'NVDY', cusip: '98851X601',
    quantity: 5, location: 'MD301', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88240',
    counted: 'DM', distributor: 'DFS', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 1, loadStatus: 'New',
    history: [{ at: '2026-06-03', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100010', materialType: 'REGULATORY', scanNo: '06082603', receivedDate: '2026-06-08',
    packingSlip: true, client: 'YieldMax', bookName: 'YieldMax COIN Option Income Strategy ETF',
    material: '30E3', documentType: '30e-3', litCode: 'N/A', ticker: 'CONY', cusip: '98851X602',
    quantity: 3, location: 'MD302', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88241',
    counted: 'RC', distributor: 'Smart', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 1, loadStatus: 'New',
    history: [{ at: '2026-06-08', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100011', materialType: 'REGULATORY', scanNo: '06072603', receivedDate: '2026-06-07',
    packingSlip: true, client: 'YieldMax', bookName: 'YieldMax Magnificent 7 Fund of Option Income ETFs',
    material: '30E3', documentType: '30e-3', litCode: 'N/A', cusip: '98851X603',
    quantity: 2, location: 'MD303', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88242',
    counted: 'DM', distributor: 'Broadridge', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 1, loadStatus: 'New',
    history: [{ at: '2026-06-07', event: 'Received — scan intake' }],
  },
  {
    receiptId: 'RCT-5100012', materialType: 'REGULATORY', scanNo: '05202601', receivedDate: '2026-05-20',
    packingSlip: true, errorCode: 'ILLEGIBLE', client: 'Kayne Anderson', bookName: 'Kayne Anderson NextGen Energy & Infrastructure Fund',
    material: 'NS', documentType: 'Prospectus', litCode: 'N/A', cusip: '486624909',
    quantity: 7, location: 'MB117', boxes: 1, carrier: 'FEDEX', tracking: '784410019101',
    counted: 'DM', distributor: 'Donnelly', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    notes: 'No candidate in Reg Ref Number Report — cover page partially illegible.',
    history: [{ at: '2026-05-20', event: 'Received — scan intake (SLA breached, no match found)' }],
  },
  {
    receiptId: 'RCT-5100013', materialType: 'REGULATORY', scanNo: '05262601', receivedDate: '2026-05-26',
    packingSlip: true, client: 'American Funds', bookName: 'American Funds Strategic Bond Fund',
    material: 'NS', documentType: 'Supplement', litCode: 'N/A', cusip: '025080909',
    quantity: 11, location: 'MB118', boxes: 1, carrier: 'UPS', tracking: '1Z9991A0499',
    counted: 'RC', distributor: 'Mittera', jobIds: [], jobStatus: 'UNIDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    notes: 'No candidate in Reg Ref Number Report — escalate for manual research.',
    history: [{ at: '2026-05-26', event: 'Received — scan intake (SLA breached, no match found)' }],
  },

  // ── IDENTIFIED (8) ──
  {
    receiptId: 'RCT-5100014', materialType: 'REGULATORY', scanNo: '06072604', receivedDate: '2026-06-07',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan Tax Free Bond Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'JPMSUM2-26', cusip: '46641Q303',
    quantity: 20, location: 'MB112', boxes: 1, carrier: 'FEDEX', tracking: '784410020011',
    counted: 'DM', distributor: 'Donnelly', jobIds: ['3611202'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 6, loadStatus: 'Loading Estimates',
    eventId: 'EVT-11202', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-06-01', mailByDate: '2026-06-18',
    history: [
      { at: '2026-06-07', event: 'Received — scan intake' },
      { at: '2026-06-07', event: 'Identified — Job # 3611202 (exact CUSIP + Lit code match)' },
    ],
  },
  {
    receiptId: 'RCT-5100015', materialType: 'REGULATORY', scanNo: '06062603', receivedDate: '2026-06-06',
    packingSlip: true, client: 'First Trust', bookName: 'First Trust Preferred Securities Income ETF',
    material: 'PROS', documentType: 'Prospectus', litCode: 'FTPROS6-26', cusip: '33739Q303',
    quantity: 35, location: 'MC201', boxes: 2, carrier: 'UPS', tracking: '1Z9991A0522',
    counted: 'RC', distributor: 'Broadridge', jobIds: ['3622302'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'Positions Estimated',
    eventId: 'EVT-22302', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-30', mailByDate: '2026-06-16',
    history: [
      { at: '2026-06-06', event: 'Received — scan intake' },
      { at: '2026-06-06', event: 'Identified — Job # 3622302 (exact CUSIP + Lit code match)' },
    ],
  },
  {
    receiptId: 'RCT-5100016', materialType: 'REGULATORY', scanNo: '06082604', receivedDate: '2026-06-08',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson Renewable Infrastructure Fund',
    material: 'SUPP', documentType: 'Supplement', litCode: 'KAESUPP3-26', cusip: '486624303',
    quantity: 14, location: 'MB114', boxes: 1, carrier: 'FEDEX', tracking: '784410020033',
    counted: 'DM', distributor: 'Mittera', jobIds: ['3633402'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 3, loadStatus: 'New',
    eventId: 'EVT-33402', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-06-02', mailByDate: '2026-06-20',
    history: [
      { at: '2026-06-08', event: 'Received — scan intake' },
      { at: '2026-06-08', event: 'Identified — Job # 3633402 (exact CUSIP + Lit code match)' },
    ],
  },
  {
    receiptId: 'RCT-5100017', materialType: 'REGULATORY', scanNo: '06052602', receivedDate: '2026-06-05',
    packingSlip: true, client: 'Fidelity', bookName: 'Fidelity Total Bond Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'FIDPROS7-26', cusip: '316175202',
    quantity: 55, location: 'MC203', boxes: 2, carrier: 'OTHER', tracking: 'SMART-88301',
    counted: 'RC', distributor: 'DFS', jobIds: ['3644502'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'Loading Estimates',
    eventId: 'EVT-44502', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-29', mailByDate: '2026-06-15',
    history: [
      { at: '2026-06-05', event: 'Received — scan intake' },
      { at: '2026-06-05', event: 'Identified — Job # 3644502 (exact CUSIP + Lit code match)' },
    ],
  },
  {
    receiptId: 'RCT-5100018', materialType: 'REGULATORY', scanNo: '06082605', receivedDate: '2026-06-08',
    packingSlip: true, client: 'Fidelity', bookName: 'Fidelity Series Investment Grade Bond Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'FIDSUM3-26', cusip: '316175303',
    quantity: 8, location: 'MC203', boxes: 1, carrier: 'FEDEX', tracking: '784410020055',
    counted: 'DM', distributor: 'Smart', jobIds: ['3644503'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 5, loadStatus: 'Positions Estimated',
    eventId: 'EVT-44503', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-06-03', mailByDate: '2026-06-19',
    history: [
      { at: '2026-06-08', event: 'Received — scan intake' },
      { at: '2026-06-08', event: 'Identified — Job # 3644503 (exact CUSIP + Lit code match)' },
    ],
  },
  {
    receiptId: 'RCT-5100019', materialType: 'REGULATORY', scanNo: '06072605', receivedDate: '2026-06-07',
    packingSlip: true, client: 'American Funds', bookName: 'American Funds American Balanced Fund',
    material: 'POST_SALE', documentType: 'Post-Sale', litCode: 'AFPS5-26', cusip: '025080303',
    quantity: 26, location: 'MB116', boxes: 1, carrier: 'UPS', tracking: '1Z9991A0588',
    counted: 'RC', distributor: 'Donnelly', jobIds: ['3655602'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'POST_SALE', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-55602', eventType: 'Regulatory Post-Sale Mailing', recordDate: '2026-05-28', mailByDate: '2026-06-14',
    history: [
      { at: '2026-06-07', event: 'Received — scan intake' },
      { at: '2026-06-07', event: 'Identified — Job # 3655602 (exact CUSIP + Lit code match)' },
    ],
  },
  {
    receiptId: 'RCT-5100020', materialType: 'REGULATORY', scanNo: '06062604', receivedDate: '2026-06-06',
    packingSlip: true, client: 'YieldMax', bookName: 'YieldMax TSLA Option Income Strategy ETF',
    material: '30E3', documentType: '30e-3', litCode: 'N/A', ticker: 'TSLY', cusip: '98851X701',
    quantity: 4, location: 'MD301', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88355',
    counted: 'DM', distributor: 'Broadridge', jobIds: ['3666702'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 1, loadStatus: 'Loading Estimates',
    eventId: 'EVT-66702', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-31', mailByDate: '2026-06-17',
    history: [
      { at: '2026-06-06', event: 'Received — scan intake' },
      { at: '2026-06-06', event: 'Identified — Job # 3666702 (manual fuzzy match confirmed)' },
    ],
  },
  {
    receiptId: 'RCT-5100021', materialType: 'REGULATORY', scanNo: '06032602', receivedDate: '2026-06-03',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan Equity Income Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'JPMSUM5-26', cusip: '46641Q404',
    quantity: 19, location: 'MB112', boxes: 1, carrier: 'FEDEX', tracking: '784410020077',
    counted: 'RC', distributor: 'Mittera', jobIds: ['3611203'], jobStatus: 'IDENTIFIED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-11203', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-27', mailByDate: '2026-06-13',
    history: [
      { at: '2026-06-03', event: 'Received — scan intake' },
      { at: '2026-06-03', event: 'Identified — Job # 3611203 (exact CUSIP + Lit code match)' },
    ],
  },

  // ── EXCEPTION (6): 2 FUND_NAME, 2 DATE_DISCREPANCY, 2 SHORTAGE ──
  {
    receiptId: 'RCT-5100022', materialType: 'REGULATORY', scanNo: '06072606', receivedDate: '2026-06-07',
    packingSlip: true, client: 'First Trust', bookName: 'First Trust Dividend Growth Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'FTPROS9-26', cusip: '33739Q404',
    quantity: 17, location: 'MC201', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88402',
    counted: 'DM', distributor: 'DFS', jobIds: ['3622303'], jobStatus: 'EXCEPTION', exceptionReason: 'FUND_NAME',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-22303', eventType: 'Regulatory (Interim) Mailing',
    notes: "Book name on piece doesn't match Job # 3622303 candidate fund — needs verification.",
    history: [
      { at: '2026-06-07', event: 'Received — scan intake' },
      { at: '2026-06-07', event: 'Flagged exception — fund-name mismatch vs Job # 3622303' },
    ],
  },
  {
    receiptId: 'RCT-5100023', materialType: 'REGULATORY', scanNo: '06082606', receivedDate: '2026-06-08',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson MLP Investment Company',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'KAESUM8-26', cusip: '486624404',
    quantity: 6, location: 'MB114', boxes: 1, carrier: 'FEDEX', tracking: '784410020099',
    counted: 'RC', distributor: 'Smart', jobIds: ['3633403'], jobStatus: 'EXCEPTION', exceptionReason: 'FUND_NAME',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 4, loadStatus: 'New',
    eventId: 'EVT-33403', eventType: 'Regulatory (Interim) Mailing',
    notes: 'Fund name ambiguous vs a similarly named sister fund on Job # 3633403.',
    history: [
      { at: '2026-06-08', event: 'Received — scan intake' },
      { at: '2026-06-08', event: 'Flagged exception — fund-name mismatch vs Job # 3633403' },
    ],
  },
  {
    receiptId: 'RCT-5100024', materialType: 'REGULATORY', scanNo: '06032603', receivedDate: '2026-06-03',
    packingSlip: true, client: 'Fidelity', bookName: 'Fidelity Floating Rate High Income Fund',
    material: 'SUPP', documentType: 'Supplement', litCode: 'FIDSUPP1-26', cusip: '316175404',
    quantity: 13, effectiveDate: '2026-05-15', location: 'MC203', boxes: 1, carrier: 'UPS', tracking: '1Z9991A0611',
    counted: 'DM', distributor: 'Donnelly', jobIds: ['3644504'], jobStatus: 'EXCEPTION', exceptionReason: 'DATE_DISCREPANCY',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-44504', eventType: 'Regulatory (Interim) Mailing',
    notes: "Effective date on piece (5/15) doesn't match Job # 3644504's record/effective date on file.",
    history: [
      { at: '2026-06-03', event: 'Received — scan intake' },
      { at: '2026-06-03', event: 'Flagged exception — record/effective-date discrepancy vs Job # 3644504' },
    ],
  },
  {
    receiptId: 'RCT-5100025', materialType: 'REGULATORY', scanNo: '06062605', receivedDate: '2026-06-06',
    packingSlip: true, client: 'American Funds', bookName: 'American Funds New Perspective Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'AFPROS8-26', cusip: '025080404',
    quantity: 24, effectiveDate: '2026-06-01', location: 'MB116', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88450',
    counted: 'RC', distributor: 'Mittera', jobIds: ['3655603'], jobStatus: 'EXCEPTION', exceptionReason: 'DATE_DISCREPANCY',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-55603', eventType: 'Regulatory (Interim) Mailing',
    notes: "Effective date on piece doesn't match Job # 3655603's record/effective date on file.",
    history: [
      { at: '2026-06-06', event: 'Received — scan intake' },
      { at: '2026-06-06', event: 'Flagged exception — record/effective-date discrepancy vs Job # 3655603' },
    ],
  },
  {
    receiptId: 'RCT-5100026', materialType: 'REGULATORY', scanNo: '06052603', receivedDate: '2026-06-05',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan High Yield Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'JPMPROS1-26', cusip: '46641Q505',
    quantity: 45, location: 'MB112', boxes: 2, carrier: 'FEDEX', tracking: '784410020101',
    counted: 'DM', distributor: 'Broadridge', jobIds: [], jobStatus: 'EXCEPTION', exceptionReason: 'SHORTAGE',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    notes: 'Received 45 of 60 expected — flagged shortage pending reorder; not yet identified.',
    history: [
      { at: '2026-06-05', event: 'Received — scan intake' },
      { at: '2026-06-05', event: 'Flagged exception — shortage (45 of 60 expected)' },
    ],
  },
  {
    receiptId: 'RCT-5100027', materialType: 'REGULATORY', scanNo: '06082607', receivedDate: '2026-06-08',
    packingSlip: true, client: 'YieldMax', bookName: 'YieldMax MSTR Option Income Strategy ETF',
    material: '30E3', documentType: '30e-3', litCode: 'N/A', ticker: 'MSTY', cusip: '98851X801',
    quantity: 2, location: 'MD301', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88477',
    counted: 'RC', distributor: 'DFS', jobIds: ['3666703'], jobStatus: 'EXCEPTION', exceptionReason: 'SHORTAGE',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 1, loadStatus: 'New',
    eventId: 'EVT-66703', eventType: 'Regulatory (Interim) Mailing',
    notes: 'Received 2 of 3 expected — flagged shortage pending reorder.',
    history: [
      { at: '2026-06-08', event: 'Received — scan intake' },
      { at: '2026-06-08', event: 'Identified — Job # 3666703' },
      { at: '2026-06-08', event: 'Flagged exception — shortage (2 of 3 expected)' },
    ],
  },

  // ── ROUTED_TO_CS (5): qty >= 200 — informational routing, never blocked ──
  {
    receiptId: 'RCT-5100028', materialType: 'REGULATORY', scanNo: '06062606', receivedDate: '2026-06-06',
    packingSlip: true, client: 'First Trust', bookName: 'First Trust Large Cap Growth AlphaDEX Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'FTSUM4-26', cusip: '33739Q505',
    quantity: 320, location: 'MC201', boxes: 6, carrier: 'FEDEX', tracking: '784410020200',
    counted: 'DM', distributor: 'Broadridge', jobIds: ['3622304'], jobStatus: 'ROUTED_TO_CS',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-22304', eventType: 'Regulatory (Interim) Mailing',
    notes: 'Qty 320 is 200 pieces or more, so it was routed to CS (informational; Regulatory Cell does not process).',
    history: [
      { at: '2026-06-06', event: 'Received — scan intake' },
      { at: '2026-06-06', event: 'Identified — Job # 3622304' },
      { at: '2026-06-06', event: 'Routed to CS, quantity 200 or more' },
    ],
  },
  {
    receiptId: 'RCT-5100029', materialType: 'REGULATORY', scanNo: '06072607', receivedDate: '2026-06-07',
    packingSlip: true, client: 'Fidelity', bookName: 'Fidelity 500 Index Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'FIDPROS2-26', cusip: '316175505',
    quantity: 500, location: 'MC203', boxes: 9, carrier: 'UPS', tracking: '1Z9991A0700',
    counted: 'RC', distributor: 'Donnelly', jobIds: ['3644505'], jobStatus: 'ROUTED_TO_CS',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-44505', eventType: 'Regulatory (Interim) Mailing',
    notes: 'Qty 500 is 200 pieces or more, so it was routed to CS (informational; Regulatory Cell does not process).',
    history: [
      { at: '2026-06-07', event: 'Received — scan intake' },
      { at: '2026-06-07', event: 'Identified — Job # 3644505' },
      { at: '2026-06-07', event: 'Routed to CS, quantity 200 or more' },
    ],
  },
  {
    receiptId: 'RCT-5100030', materialType: 'REGULATORY', scanNo: '06052604', receivedDate: '2026-06-05',
    packingSlip: true, client: 'American Funds', bookName: 'American Funds Growth Fund of America',
    material: 'PROS', documentType: 'Prospectus', litCode: 'AFPROS2-26', cusip: '025080505',
    quantity: 275, location: 'MB116', boxes: 5, carrier: 'OTHER', tracking: 'SMART-88500',
    counted: 'DM', distributor: 'Mittera', jobIds: ['3655604'], jobStatus: 'ROUTED_TO_CS',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    eventId: 'EVT-55604', eventType: 'Regulatory (Interim) Mailing',
    notes: 'Qty 275 is 200 pieces or more, so it was routed to CS (informational; Regulatory Cell does not process).',
    history: [
      { at: '2026-06-05', event: 'Received — scan intake' },
      { at: '2026-06-05', event: 'Identified — Job # 3655604' },
      { at: '2026-06-05', event: 'Routed to CS, quantity 200 or more' },
    ],
  },
  {
    receiptId: 'RCT-5100031', materialType: 'REGULATORY', scanNo: '06082608', receivedDate: '2026-06-08',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan Large Cap Growth Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'JPMSUM7-26', cusip: '46641Q606',
    quantity: 410, location: 'MB112', boxes: 7, carrier: 'FEDEX', tracking: '784410020255',
    counted: 'RC', distributor: 'Smart', jobIds: [], jobStatus: 'ROUTED_TO_CS',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 6, loadStatus: 'New',
    notes: 'Qty 410 is 200 pieces or more, so it was routed to CS before identification (informational; never blocked).',
    history: [
      { at: '2026-06-08', event: 'Received — scan intake' },
      { at: '2026-06-08', event: 'Routed to CS before identification, quantity 200 or more' },
    ],
  },
  {
    receiptId: 'RCT-5100032', materialType: 'REGULATORY', scanNo: '06042602', receivedDate: '2026-06-04',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson NextGen Infrastructure II Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'N/A', cusip: '486624505',
    quantity: 260, location: 'MB114', boxes: 5, carrier: 'UPS', tracking: '1Z9991A0733',
    counted: 'DM', distributor: 'Donnelly', jobIds: [], jobStatus: 'ROUTED_TO_CS',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'New',
    notes: 'Qty 260 is 200 pieces or more, so it was routed to CS before identification (informational; never blocked).',
    history: [
      { at: '2026-06-04', event: 'Received — scan intake' },
      { at: '2026-06-04', event: 'Routed to CS before identification, quantity 200 or more' },
    ],
  },

  // ── IN_PRODUCTION (4) — Identified, mail bucket created, not yet printed ──
  {
    receiptId: 'RCT-5100033', materialType: 'REGULATORY', scanNo: '06042603', receivedDate: '2026-06-04',
    packingSlip: true, client: 'First Trust', bookName: 'First Trust Senior Floating Rate ETF',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'FTSUM1-26', cusip: '33739Q606',
    quantity: 30, location: 'MC201', boxes: 1, carrier: 'FEDEX', tracking: '784410020300',
    counted: 'DM', distributor: 'DFS', jobIds: ['3622305'], jobStatus: 'IN_PRODUCTION',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'Loading Final Positions',
    eventId: 'EVT-22305', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-29', mailByDate: '2026-06-12',
    bucket: { bucketId: 'BKT-22305', packageType: '6x9', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 30, qtyReceived: 30, printed: false },
    history: [
      { at: '2026-06-04', event: 'Received — scan intake' },
      { at: '2026-06-04', event: 'Identified — Job # 3622305' },
      { at: '2026-06-05', event: 'Mail bucket BKT-22305 created — In Production' },
    ],
  },
  {
    receiptId: 'RCT-5100034', materialType: 'REGULATORY', scanNo: '06062607', receivedDate: '2026-06-06',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson Diversified Energy Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'KAEPROS5-26', cusip: '486624606',
    quantity: 45, location: 'MB114', boxes: 1, carrier: 'UPS', tracking: '1Z9991A0755',
    counted: 'RC', distributor: 'Smart', jobIds: ['3633404'], jobStatus: 'IN_PRODUCTION',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 10, loadStatus: 'Position Final',
    eventId: 'EVT-33404', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-06-01', mailByDate: '2026-06-14',
    bucket: { bucketId: 'BKT-33404', packageType: '9x12', mailMethod: 'Pylon Logistic Standard', indicia: 'other (see comment)', indiciaComment: 'Pylon Logistic Indicia-RC', qtyOrdered: 45, qtyReceived: 45, printed: false },
    history: [
      { at: '2026-06-06', event: 'Received — scan intake' },
      { at: '2026-06-06', event: 'Identified — Job # 3633404' },
      { at: '2026-06-07', event: 'Mail bucket BKT-33404 created — In Production' },
    ],
  },
  {
    receiptId: 'RCT-5100035', materialType: 'REGULATORY', scanNo: '06072608', receivedDate: '2026-06-07',
    packingSlip: true, client: 'Fidelity', bookName: 'Fidelity Blue Chip Growth Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'FIDSUM6-26', cusip: '316175606',
    quantity: 22, location: 'MC203', boxes: 1, carrier: 'FEDEX', tracking: '784410020322',
    counted: 'DM', distributor: 'Donnelly', jobIds: ['3644506'], jobStatus: 'IN_PRODUCTION',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'Position Final',
    eventId: 'EVT-44506', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-06-02', mailByDate: '2026-06-16',
    bucket: { bucketId: 'BKT-44506', packageType: '#10', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 22, qtyReceived: 22, printed: false },
    history: [
      { at: '2026-06-07', event: 'Received — scan intake' },
      { at: '2026-06-07', event: 'Identified — Job # 3644506' },
      { at: '2026-06-08', event: 'Mail bucket BKT-44506 created — In Production' },
    ],
  },
  {
    receiptId: 'RCT-5100036', materialType: 'REGULATORY', scanNo: '06052605', receivedDate: '2026-06-05',
    packingSlip: true, client: 'American Funds', bookName: 'American Funds Income Fund of America',
    material: 'POST_SALE', documentType: 'Post-Sale', litCode: 'AFPS9-26', cusip: '025080606',
    quantity: 33, location: 'MB116', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88560',
    counted: 'RC', distributor: 'Mittera', jobIds: ['3655605'], jobStatus: 'IN_PRODUCTION',
    postSaleOrInterim: 'POST_SALE', materialFormat: 'PHYSICAL', loadStatus: 'Loading Final Positions',
    eventId: 'EVT-55605', eventType: 'Regulatory Post-Sale Mailing', recordDate: '2026-05-30', mailByDate: '2026-06-13',
    bucket: { bucketId: 'BKT-55605', packageType: '6x9 Flat', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 33, qtyReceived: 33, printed: false },
    history: [
      { at: '2026-06-05', event: 'Received — scan intake' },
      { at: '2026-06-05', event: 'Identified — Job # 3655605' },
      { at: '2026-06-06', event: 'Mail bucket BKT-55605 created — In Production' },
    ],
  },

  // ── PRINTED (8) ──
  {
    receiptId: 'RCT-5100037', materialType: 'REGULATORY', scanNo: '06012601', receivedDate: '2026-06-01',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan Short Duration Bond Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'JPMSUM1-26', cusip: '46641Q707',
    quantity: 16, location: 'MB112', boxes: 1, carrier: 'FEDEX', tracking: '784410019800',
    counted: 'DM', distributor: 'Broadridge', jobIds: ['3611204'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 5, loadStatus: 'Position Final',
    eventId: 'EVT-11204', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-25', mailByDate: '2026-06-08',
    bucket: { bucketId: 'BKT-11204', packageType: '#10', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 16, qtyReceived: 16, printed: true },
    history: [
      { at: '2026-06-01', event: 'Received — scan intake' },
      { at: '2026-06-01', event: 'Identified — Job # 3611204' },
      { at: '2026-06-02', event: 'Mail bucket BKT-11204 created — In Production' },
      { at: '2026-06-03', event: 'Printed' },
    ],
  },
  {
    receiptId: 'RCT-5100038', materialType: 'REGULATORY', scanNo: '06022601', receivedDate: '2026-06-02',
    packingSlip: true, client: 'First Trust', bookName: 'First Trust Water ETF',
    material: 'PROS', documentType: 'Prospectus', litCode: 'FTPROS3-26', cusip: '33739Q707',
    quantity: 28, location: 'MC201', boxes: 1, carrier: 'UPS', tracking: '1Z9991A0800',
    counted: 'RC', distributor: 'Donnelly', jobIds: ['3622306'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'Position Final',
    eventId: 'EVT-22306', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-27', mailByDate: '2026-06-09',
    bucket: { bucketId: 'BKT-22306', packageType: '9x12', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 28, qtyReceived: 28, printed: true },
    history: [
      { at: '2026-06-02', event: 'Received — scan intake' },
      { at: '2026-06-02', event: 'Identified — Job # 3622306' },
      { at: '2026-06-03', event: 'Mail bucket BKT-22306 created — In Production' },
      { at: '2026-06-04', event: 'Printed' },
    ],
  },
  {
    receiptId: 'RCT-5100039', materialType: 'REGULATORY', scanNo: '06032604', receivedDate: '2026-06-03',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson Midstream Energy Fund',
    material: 'SUPP', documentType: 'Supplement', litCode: 'KAESUPP6-26', cusip: '486624707',
    quantity: 10, location: 'MB114', boxes: 1, carrier: 'FEDEX', tracking: '784410019822',
    counted: 'DM', distributor: 'Mittera', jobIds: ['3633405'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 3, loadStatus: 'Position Final',
    eventId: 'EVT-33405', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-28', mailByDate: '2026-06-10',
    bucket: { bucketId: 'BKT-33405', packageType: '6x9', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 10, qtyReceived: 10, printed: true },
    history: [
      { at: '2026-06-03', event: 'Received — scan intake' },
      { at: '2026-06-03', event: 'Identified — Job # 3633405' },
      { at: '2026-06-04', event: 'Mail bucket BKT-33405 created — In Production' },
      { at: '2026-06-05', event: 'Printed' },
    ],
  },
  {
    receiptId: 'RCT-5100040', materialType: 'REGULATORY', scanNo: '06042604', receivedDate: '2026-06-04',
    packingSlip: true, client: 'Fidelity', bookName: 'Fidelity Strategic Income Fund',
    material: 'PROS', documentType: 'Prospectus', litCode: 'FIDPROS5-26', cusip: '316175707',
    quantity: 38, location: 'MC203', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88600',
    counted: 'RC', distributor: 'Smart', jobIds: ['3644507'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'PHYSICAL', loadStatus: 'Position Final',
    eventId: 'EVT-44507', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-29', mailByDate: '2026-06-11',
    bucket: { bucketId: 'BKT-44507', packageType: '#10', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 38, qtyReceived: 38, printed: true },
    history: [
      { at: '2026-06-04', event: 'Received — scan intake' },
      { at: '2026-06-04', event: 'Identified — Job # 3644507' },
      { at: '2026-06-05', event: 'Mail bucket BKT-44507 created — In Production' },
      { at: '2026-06-06', event: 'Printed' },
    ],
  },
  {
    receiptId: 'RCT-5100041', materialType: 'REGULATORY', scanNo: '06052606', receivedDate: '2026-06-05',
    packingSlip: true, client: 'American Funds', bookName: 'American Funds Bond Fund of America',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'AFSUM6-26', cusip: '025080707',
    quantity: 21, location: 'MB116', boxes: 1, carrier: 'FEDEX', tracking: '784410019844',
    counted: 'DM', distributor: 'DFS', jobIds: ['3655606'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 5, loadStatus: 'Position Final',
    eventId: 'EVT-55606', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-30', mailByDate: '2026-06-12',
    bucket: { bucketId: 'BKT-55606', packageType: '6x9 Flat', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 21, qtyReceived: 21, printed: true },
    history: [
      { at: '2026-06-05', event: 'Received — scan intake' },
      { at: '2026-06-05', event: 'Identified — Job # 3655606' },
      { at: '2026-06-06', event: 'Mail bucket BKT-55606 created — In Production' },
      { at: '2026-06-07', event: 'Printed' },
    ],
  },
  {
    receiptId: 'RCT-5100042', materialType: 'REGULATORY', scanNo: '06012602', receivedDate: '2026-06-01',
    packingSlip: true, client: 'YieldMax', bookName: 'YieldMax GOOGL Option Income Strategy ETF',
    material: '30E3', documentType: '30e-3', litCode: 'N/A', ticker: 'GOOY', cusip: '98851X902',
    quantity: 3, location: 'MD301', boxes: 1, carrier: 'OTHER', tracking: 'SMART-88622',
    counted: 'RC', distributor: 'Broadridge', jobIds: ['3666704'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 1, loadStatus: 'Position Final',
    eventId: 'EVT-66704', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-26', mailByDate: '2026-06-08',
    bucket: { bucketId: 'BKT-66704', packageType: '#10', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 3, qtyReceived: 3, printed: true },
    history: [
      { at: '2026-06-01', event: 'Received — scan intake' },
      { at: '2026-06-01', event: 'Identified — Job # 3666704 (manual fuzzy match confirmed)' },
      { at: '2026-06-02', event: 'Mail bucket BKT-66704 created — In Production' },
      { at: '2026-06-08', event: 'Printed' },
    ],
  },
  {
    receiptId: 'RCT-5100043', materialType: 'REGULATORY', scanNo: '06022602', receivedDate: '2026-06-02',
    packingSlip: true, client: 'JPMorgan', bookName: 'JPMorgan Municipal Income Fund',
    material: 'POST_SALE', documentType: 'Post-Sale', litCode: 'JPMPS3-26', cusip: '46641Q808',
    quantity: 50, location: 'MB112', boxes: 2, carrier: 'UPS', tracking: '1Z9991A0866',
    counted: 'DM', distributor: 'Mittera', jobIds: ['3611205'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'POST_SALE', materialFormat: 'PHYSICAL', loadStatus: 'Position Final',
    eventId: 'EVT-11205', eventType: 'Regulatory Post-Sale Mailing', recordDate: '2026-05-27', mailByDate: '2026-06-09',
    bucket: { bucketId: 'BKT-11205', packageType: '9x12', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 50, qtyReceived: 50, printed: true },
    history: [
      { at: '2026-06-02', event: 'Received — scan intake' },
      { at: '2026-06-02', event: 'Identified — Job # 3611205' },
      { at: '2026-06-03', event: 'Mail bucket BKT-11205 created — In Production' },
      { at: '2026-06-04', event: 'Printed' },
    ],
  },
  {
    receiptId: 'RCT-5100044', materialType: 'REGULATORY', scanNo: '06032605', receivedDate: '2026-06-03',
    packingSlip: true, client: 'Kayne Anderson', bookName: 'Kayne Anderson Floating Rate Income Fund',
    material: 'SP', documentType: 'Summary Prospectus', litCode: 'KAESUM2-26', cusip: '486624808',
    quantity: 14, location: 'MB114', boxes: 1, carrier: 'FEDEX', tracking: '784410019866',
    counted: 'RC', distributor: 'Donnelly', jobIds: ['3633406'], jobStatus: 'PRINTED',
    postSaleOrInterim: 'INTERIM', materialFormat: 'POD', pagesPerDoc: 4, loadStatus: 'Position Final',
    eventId: 'EVT-33406', eventType: 'Regulatory (Interim) Mailing', recordDate: '2026-05-28', mailByDate: '2026-06-10',
    bucket: { bucketId: 'BKT-33406', packageType: '6x9', mailMethod: 'Pylon Logistic Standard', indicia: 'No Indicia', qtyOrdered: 14, qtyReceived: 14, printed: true },
    history: [
      { at: '2026-06-03', event: 'Received — scan intake' },
      { at: '2026-06-03', event: 'Identified — Job # 3633406' },
      { at: '2026-06-04', event: 'Mail bucket BKT-33406 created — In Production' },
      { at: '2026-06-08', event: 'Printed' },
    ],
  },
];
